import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useGetAgencies } from "../../hooks/agencies/useGetAgencies";
import { useDeleteAgency } from "../../hooks/agencies/useDeleteAgency";
import { useMassDestroyAgencies } from "../../hooks/agencies/useMassDestroyAgencies";
import { useMassRestoreAgencies } from "../../hooks/agencies/useMassRestoreAgencies";
import { useMassForceDeleteAgencies } from "../../hooks/agencies/useMassForceDeleteAgencies";
import { useDuplicateAgency } from "../../hooks/agencies/useDuplicateAgency";

import useTableActions from "../../hooks/common/useTableActions";
import TableLayout from "../global/Table/TableLayout";
import AgenciesTableRow from "./Table/AgenciesTableRow";
import { AgenciesTableColumns } from "./Table/AgenciesTableColumns";
import { usePermissions } from "../../auth/PermissionsContext";

const AgenciesTable = () => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { has } = usePermissions();

    const perms = useMemo(
        () => ({
            list: has("viewAny-agency"),
            create: has("create-agency"),
            edit: has("update-agency"),
            delete: has("delete-agency"),
        }),
        [has]
    );

    const navigate = useNavigate();

    // Filter States
    const [trashedFilter, setTrashedFilter] = useState("");

    // CRUD Hooks
    const { mutate: deleteAgencyMutate, isPending: isDelete } = useDeleteAgency();
    const { mutate: massDestroyAgenciesMutate, isPending: isDeleteAll } = useMassDestroyAgencies();
    const { mutate: massRestoreAgenciesMutate, isPending: isRestoreAll } = useMassRestoreAgencies();
    const { mutate: massForceDeleteAgenciesMutate, isPending: isForceDeleteAll } = useMassForceDeleteAgencies();
    const { mutate: duplicateAgencyMutate } = useDuplicateAgency();

    const {
        page,
        perPage,
        search,
        order,
        selectedIds,
        menuAnchorEl,
        setSearch,
        handleRequestSort,
        handleRowCheckboxClick,
        handleSelectAllClick,
        handleMenuOpen,
        handleMenuClose,
        handleEdit,
        handleMenuDelete,
        handleMenuRestore,
        handleMenuDuplicate,
        handleBulkDelete,
        handleBulkRestore,
        handlePageChange,
        handlePerPageChange,
    } = useTableActions({
        basePath: "/agencies",
        deleteSingle: deleteAgencyMutate,
        deleteAll: massDestroyAgenciesMutate,
        restoreSingle: (id, options) => massRestoreAgenciesMutate({ items: [id] }, options),
        restoreAll: massRestoreAgenciesMutate,
        forceDeleteSingle: (id, options) => massForceDeleteAgenciesMutate({ items: [id] }, options),
        forceDeleteAll: massForceDeleteAgenciesMutate,
        duplicateSingle: (id, options) => duplicateAgencyMutate(id, options),
        isArchive: trashedFilter === "only",
    });

    const params = useMemo(
        () => ({
            page,
            per_page: perPage,
            search: search || undefined,
            trashed: trashedFilter === "only" ? "only" : undefined,
            sort_by: 'created_at',
        }),
        [page, perPage, search, trashedFilter]
    );

    const { isLoading, error, data } = useGetAgencies(params);
    const agencies = data?.data || [];
    const meta = data?.meta || {};

    // No additional filters besides archive
    const filters = [];

    const columns = useMemo(() => AgenciesTableColumns(isArabic), [isArabic]);

    return (
        <TableLayout
            title={{ ar: "الوكالات", en: "Agencies" }}
            columns={columns}
            items={agencies}
            meta={meta}
            isLoading={isLoading}
            error={error}
            perms={perms}
            filters={filters}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            onSelectAllClick={(e) => handleSelectAllClick(e, agencies)}
            onRowCheckboxClick={handleRowCheckboxClick}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            onBulkDelete={handleBulkDelete}
            onBulkRestore={handleBulkRestore}
            isBulkDeleting={isDeleteAll || isForceDeleteAll}
            isBulkRestoring={isRestoreAll}
            isArchive={trashedFilter === "only"}
            onAddClick={() => navigate("/agencies/create")}
            addButtonLabel={{ ar: "إضافة وكالة", en: "Add Agency" }}
            page={page}
            perPage={perPage}
            order={order}
            onRequestSort={handleRequestSort}
            menuAnchorEl={menuAnchorEl}
            openMenu={Boolean(menuAnchorEl)}
            onMenuClose={handleMenuClose}
            onMenuEdit={handleEdit}
            onMenuDelete={handleMenuDelete}
            onMenuRestore={handleMenuRestore}
            onMenuDuplicate={handleMenuDuplicate}
            onArchiveToggle={() => setTrashedFilter(prev => prev === "only" ? "" : "only")}
            isDeletingSingle={isDelete || isForceDeleteAll}
            renderRow={(params) => (
                <AgenciesTableRow
                    {...params}
                    key={params.item.id}
                    agency={params.item}
                    perms={perms}
                    onMenuOpen={handleMenuOpen}
                />
            )}
        />
    );
};

export default AgenciesTable;
